import type { GroupedPlayerRewardDto, RewardTypeValue } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { useQuery } from '@tanstack/react-query';
import { playerQueryOptions } from '@/queries/player/player.queries';
import { playerGamificationRewardsOptions } from '@/queries/playerGamificationRewards/playerGamificationRewards.queries';
import { GamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { progressStatusesQueryOptions } from '@/queries/progress-statuses/progress-statuses.queries';
import { gamificationMockRewards } from '../constants/gamificationRewardCards';

const FIRST_PLAYER_LEVEL = 1;

// A card unlocks once the player reaches its grouped reward's minimum level.
// The Level-up Scratch Card is the exception: it is already available on the first level.
const isRewardCardDisabled = (
  type: RewardTypeValue | undefined,
  groupedReward: GroupedPlayerRewardDto | undefined,
  playerLevel: number,
) => {
  if (type === GamificationRewardType.LevelUp && playerLevel === FIRST_PLAYER_LEVEL) {
    return false;
  }

  return !groupedReward?.minLevelFrom || !groupedReward || playerLevel < groupedReward.minLevelFrom;
};

// Builds the Rewards grid cards from the player's level, the progress statuses and the
// player's grouped rewards: each card gets its availability and the status it requires.

export const useGamificationRewardCards = () => {
  const { data: playerProfile, isPending: isPlayerProfilePending } = useQuery(playerQueryOptions.mePlayerProfile());
  const { data: progressStatuses, isPending: isProgressStatusesPending } = useQuery(progressStatusesQueryOptions.list());

  const { data: groupedRewards, isPending: isGroupedRewardsPending } = useQuery(playerGamificationRewardsOptions.meGroupedRewards());

  const playerLevel = playerProfile?.level ?? FIRST_PLAYER_LEVEL;

  // Both lists are short (one grouped reward per reward type, a handful of statuses),
  // so a direct lookup per card is enough: no index maps or memoized dependencies to keep in sync.
  const cards = gamificationMockRewards.map(({ action, type, ...reward }) => {
    const groupedReward = groupedRewards?.find(item => item.type === type);
    const requiredStatus = groupedReward
      ? progressStatuses?.items.find(status => status.levelFrom === groupedReward.minLevelFrom)
      : undefined;
    const statusName = requiredStatus?.name;
    const isRewardDisabled = isRewardCardDisabled(type, groupedReward, playerLevel);

    const availableAmount = (
      isRewardDisabled
        ? 0
        : groupedReward?.availableAmount
    );

    return {
      action,
      disabled: isRewardDisabled,
      reward: {
        ...reward,
        statusName,
        availableAmount,
      },
    };
  });

  return {
    cards,
    isLoading: isPlayerProfilePending || isProgressStatusesPending || isGroupedRewardsPending,
  };
};
